<template>
  <div :class="containerVariants({ theme })">
    <Container class="px-0 sm:px-4">
      <div class="mb-16 mt-4">
        <Title :custom-style="titleVariants({ theme })" :level="2">{{ title }}</Title>
        <h4 v-if="subTitle" :class="subTitleVariants({ theme })">{{ subTitle }}</h4>
      </div>
      <RadioCards
        :options="presents"
        v-model="presentModel"
        :tall-cards="tallCards"
        :theme="theme"
      />

      <div
        v-if="v$?.$invalid && v$?.$dirty && v$?.$errors?.length > 0"
        class="flex w-full justify-center rounded px-1 py-5 pr-3 font-sans text-sm text-pink-act"
        role="alert"
      >
        <span v-for="err in v$.$errors" :key="err.$uid">{{ err.$message }}</span>
      </div>

      <div
        class="mt-12 flex w-full flex-col items-center justify-center gap-6 px-4 sm:px-0 xl:mt-16 xl:gap-11"
      >
        <Button
          v-if="showSkip && siteOrg !== 'raddadjuren'"
          :title="isGiftcard ? 'Jag önskar inget gåvobevis' : $t('button.skip-present')"
          :type="'outline'"
          :iconType="'arrowRight'"
          :class="skipButtonVariants({ theme })"
          @click="handleClickSkip"
        />

        <ButtonAct
          v-if="showSkip && siteOrg === 'raddadjuren'"
          :title="$t('button.skip-present')"
          :variant="'primary'"
          :isCta="false"
          :theme="theme"
          @click="handleClickSkip"
          class="-mb-4"
        />

        <Button
          v-if="siteOrg !== 'raddadjuren'"
          :title="$t('button.next')"
          color="pink"
          class="mb-16 w-full md:w-80 lg:mb-6 lg:w-96"
          @click="handleClickNext"
          :arrow-right="true"
        />
        <ButtonAct
          v-if="siteOrg === 'raddadjuren'"
          class="mb-12 lg:mb-0"
          :title="$t('button.next')"
          :theme="theme"
          :isCta="true"
          :variant="'primary'"
          :iconType="'arrowRight'"
          @click="handleClickNext"
        />
      </div>
    </Container>
  </div>
</template>

<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import type { Validation } from '@vuelidate/core'
import { cva } from 'class-variance-authority'
import RadioCards from './RadioCards.vue'
import { Button, Container, Title, THEMES } from 'refresh-ui'
import type { SiteOrg } from '../../types/env'
import ButtonAct from '../atoms/ButtonAct.vue'
import { twMerge } from 'tailwind-merge'

const props = withDefaults(
  defineProps<{
    title: string
    subTitle?: string
    present: string
    skipPresent: boolean
    presents: Array<any>
    v$: Validation
    tallCards?: boolean
    showSkip?: boolean
    theme?: THEMES
    siteOrg?: SiteOrg
    isGiftcard?: boolean
  }>(),
  {
    tallCards: false,
    showSkip: true,
    theme: THEMES.DEFAULT,
    isGiftcard: false,
  },
)

const emits = defineEmits(['update:present', 'update:skip-present', 'onNext'])

const presentModel = useVModel(props, 'present', emits)

const handleClickNext = () => {
  if (props.v$) {
    props.v$.$touch()
    props.v$.$validate()

    if (!props.v$.$invalid) emits('onNext')
    return
  }
  emits('onNext')
}

const handleClickSkip = () => {
  emits('update:skip-present', true)
  emits('onNext')
}

const containerVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'pb-16 sm:bg-blue-lighter sm:px-9 sm:pt-9 xl:py-[75px]',
      [THEMES.RDK]:
        'max-w-[1920px] bg-rdk mx-auto px-1 lg:px-4 lg:pb-12 lg:pt-8 sm:rounded-2xl pt-6 sm:mx-4 lg:mx-9 [&_*]:font-avenir xl:mx-auto',
      [THEMES.DJURENS_RATT]:
        'max-w-[1920px] w-full bg-blue-lighter lg:px-4 lg:pb-12 lg:pt-8 sm:rounded-2xl pt-6 md:pt-8 lg:mx-9 xl:mx-auto',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const titleVariants = cva('lg:text-center px-4 lg:px-0', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'mb-6 xl:mb-14',
      [THEMES.RDK]:
        'text-purple-act-dark text-h2 lg:text-h2 font-normal leading-tight font-headline mb-3 lg:mb-4',
      [THEMES.DJURENS_RATT]: twMerge(
        props.subTitle ? 'mb-3 lg:mb-4' : 'mb-10',
        'text-3xl leading-tight lg:text-5xl font-bold',
      ),
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const skipButtonVariants = cva(
  'flex w-full items-center justify-center rounded-md bg-white px-4 py-4 font-sans text-base leading-none text-black lg:w-96 xl:text-lg',
  {
    variants: {
      theme: {
        [THEMES.DEFAULT]: 'shadow-[0_4px_30px_0_rgba(0,180,210,0.23)]',
        [THEMES.RDK]: 'shadow-[0_4px_30px_0_rgba(122,121,174,0.25)] hover:bg-rdk-orange',
        [THEMES.DJURENS_RATT]:
          'shadow-[0_4px_30px_0_rgba(0,180,210,0.23)] hover:bg-blue-dark hover:text-white md:w-80 lg:w-96',
      },
    },
    defaultVariants: {
      theme: THEMES.DEFAULT,
    },
  },
)

const subTitleVariants = cva('lg:text-center px-4 lg:px-0 mb-7 text-xl leading-none lg:text-28 ', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'text-black',
      [THEMES.RDK]: ' text-purple-act-dark text-body text-h4',
      [THEMES.DJURENS_RATT]: 'text-black',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})
</script>
